/**
 * The outbox tray: what is waiting to go, and what could not go.
 *
 * THE SECOND HALF IS WHY THIS EXISTS. A message the submission server refuses —
 * a 5xx, or the retries exhausted — is marked `failed` and left in the outbox
 * table, and until this panel nothing anywhere in the product said so. The only
 * signal a sender ever got was the "Message will send in Ns" toast, and a timer
 * takes that away the moment the hold expires. A countdown reaching zero reads
 * as "sent". So the failure mode was somebody believing mail had gone that was
 * sitting in a row nobody could see, for ever.
 *
 * IT SHOWS THE STATE AND MOSTLY CANNOT CHANGE IT, deliberately rather than for
 * want of buttons. The origin's only write on this table is Undo Send, and that
 * refuses anything no longer `queued` — there is no re-queue route and no way to
 * discard a failed row. A "Retry" here would be a button that answers `too_late`
 * every time, which is worse than a sentence saying the message needs writing
 * again.
 *
 * THE TWO ORIGIN CALLS COME IN AS PROPS, and that is the one thing here that is
 * not this codebase's usual shape: every other panel reaches `api` directly.
 * `GET /outbox` has no client method yet — `api.ts` carries only `unsend` — and
 * a component importing one that does not exist would not compile. The shell
 * passes them in; when the method lands, this becomes the ordinary import.
 */
import { useCallback, useEffect, useState } from 'react'
import { HearthError, type OutboxRow } from './api.ts'
import { fullDate } from './format.ts'


export interface OutboxProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly onError: (message: string) => void
  /** `GET /outbox`. See the note at the top: this is `api.outbox` once it exists. */
  readonly load: () => Promise<{ messages: readonly OutboxRow[] }>
  /** `api.unsend`. The one write the origin offers, and only while `queued`. */
  readonly unsend: (id: number) => Promise<unknown>
}

/**
 * How often an open tray asks again.
 *
 * IT HAS TO POLL, because every state change here happens without the reader
 * doing anything: the hold expires, a pass claims the row, submission refuses
 * it. A tray that only loaded on open would show "waiting to go" beside a
 * message that had already failed — the same lie in a different place.
 */
const POLL_MS = 5000

const WAITING: ReadonlySet<string> = new Set(['queued', 'sending'])

export function Outbox({ open, onClose, onError, load, unsend }: OutboxProps): React.JSX.Element | null {
  const [rows, setRows] = useState<readonly OutboxRow[] | null>(null)
  const [busy, setBusy] = useState<number | null>(null)

  const refresh = useCallback(() => {
    load()
      .then((r) => setRows(r.messages))
      // AN EMPTY LIST IS NOT WHAT A FAILED FETCH MEANS, so it is not what is
      // shown: "Nothing is waiting" from a request that never arrived is the
      // reassurance this whole panel exists to stop giving.
      .catch((error: unknown) => {
        setRows(null)
        onError(error instanceof Error ? error.message : 'Could not read the outbox')
      })
  }, [load, onError])

  useEffect(() => {
    if (!open) return
    refresh()
    const timer = setInterval(refresh, POLL_MS)
    return () => clearInterval(timer)
  }, [open, refresh])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const bringBack = (id: number): void => {
    setBusy(id)
    unsend(id)
      .then(() => onError('Message brought back'))
      .catch((error: unknown) =>
        // `too_late` is not a failure: the message went, which is what was asked
        // for a moment ago. The list is re-read either way, because both answers
        // mean the row on screen is stale.
        onError(
          error instanceof HearthError && error.code === 'too_late'
            ? 'That message has already gone'
            : error instanceof Error
              ? error.message
              : 'That did not go through',
        ),
      )
      .finally(() => {
        setBusy(null)
        refresh()
      })
  }

  const failed = (rows ?? []).filter((row) => row.state === 'failed')
  const waiting = (rows ?? []).filter((row) => WAITING.has(row.state))

  const line = (row: OutboxRow): React.JSX.Element => (
    <>
      <span className="admin-audit-when">{fullDate(row.createdAt)}</span>
      <span className="admin-audit-what">
        {row.subject === null || row.subject === '' ? '(no subject)' : row.subject} — to {row.to.join(', ')}
      </span>
    </>
  )

  return (
    <div
      className="settings-scrim"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div className="settings-panel" role="dialog" aria-modal="true" aria-label="Outbox">
        <div className="settings-head">
          <h2 className="settings-title">Outbox</h2>
          <button className="settings-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="settings-body">
          {rows === null ? (
            <p className="screener-empty">…</p>
          ) : rows.length === 0 ? (
            <p className="screener-empty">Nothing is waiting to go.</p>
          ) : null}

          {failed.length > 0 ? (
            <section>
              {/* FIRST, AND NAMED AS A FAILURE. These are the rows somebody
                  believes were sent; a tray that led with the queue would bury
                  them under messages that are merely a few seconds old. */}
              <h3 className="admin-form-title">
                {failed.length === 1 ? 'One message could not be sent' : `${failed.length} messages could not be sent`}
              </h3>
              <p className="admin-note">
                These are still here and have not gone anywhere. Nothing in the app can send them again — write the
                message once more, and tell whoever runs the server what the error below says.
              </p>
              {/* `admin-audit-list` UNTIL THIS HAS A RULE OF ITS OWN: it is the
                  same shape, a timestamped list of things that happened, and an
                  unstyled tray is a wall of text. */}
              <ul className="admin-audit-list outbox-list">
                {failed.map((row) => (
                  <li key={row.id} className="outbox-row" data-state={row.state}>
                    {line(row)}
                    <span className="admin-warning outbox-error">
                      {row.lastError ?? 'The server gave no reason.'}
                      {row.attempts > 1 ? ` (tried ${row.attempts} times)` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {waiting.length > 0 ? (
            <section>
              <h3 className="admin-form-title">Waiting to go</h3>
              <ul className="admin-audit-list outbox-list">
                {waiting.map((row) => (
                  <li key={row.id} className="outbox-row" data-state={row.state}>
                    {line(row)}
                    {row.state === 'sending' ? (
                      // NO BUTTON WHILE IT IS GOING, because the origin refuses
                      // one: the SMTP transaction may already be open, and
                      // "cancelled" would be a lie.
                      <span className="admin-note">Going now</span>
                    ) : (
                      <button
                        type="button"
                        className="compose-cancel"
                        disabled={busy === row.id}
                        onClick={() => bringBack(row.id)}
                      >
                        Bring back
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      </div>
    </div>
  )
}
